import React, { useState, useEffect } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogContent,
  Typography,
  Drawer,
  List,
  ListItem,
} from '@mui/material';
import { styled } from '@mui/material/styles';
import { useNavigate, useLocation } from 'react-router-dom';

import { useMediaQueries, useStore } from 'hooks';
import { NotFoundIcon } from 'assets/icons';
import FullDetails from 'components/@SystemConfiguration/Subscription/SubscriptionDetailsModal/FullDetails';
import { text, details } from 'components/@SystemConfiguration/Subscription/settings';
import { useTranslation } from 'react-i18next';

const smartMonitoringBlockedRoutes = [
  '/on-tap',
  '/inventory',
  '/inventory/cooler',
  '/inventory/ordering',
  '/inventory/history',
  '/my-beverages',
  '/quality-management/cleaning-management',
  '/quality-management/cleaning-logs',
  '/reports',
  '/reports/generate',
  '/reports/pos-skus',
  '/reports/configuration',
  '/digital-menu',
  '/digital-menu/menus',
  '/digital-menu/lists',
  '/digital-menu/designs',
  '/activity-logs',
];

const noSubscriptionsBlockedRoutes = [
  ...smartMonitoringBlockedRoutes,
  '/quality-management',
  '/quality-management/system-health',
  '/employees',
  '/system-configuration/general',
  '/system-configuration/topology-management',
  '/system-configuration/alerts',
];

const SubscriptionDetailsModal = () => {
  const { t } = useTranslation();
  const { subscriptionsStore, userStore } = useStore();
  const { isTabletOrSmaller } = useMediaQueries();
  const navigate = useNavigate();
  const location = useLocation();
  const [openFullDetails, setOpenFullDetails] = useState(false);
  const [openSubscriptionBlockedModal, setOpenSubscriptionBlockedModal] = useState(false);
  const [firstAttempt, setFirstAttempt] = useState(true);

  const hasPermission = ['BarTrack', 'Owner', 'Manager'].includes(
    userStore.currentRole?._role_name,
  );

  const handleMoreDetails = () => {
    if (isTabletOrSmaller) {
      hasPermission ? setOpenFullDetails(true) : navigate('/system-configuration/subscription');
    } else {
      hasPermission
        ? navigate('/system-configuration/subscription/details', {
            state: { type: 'smartDraft' },
          })
        : navigate('/system-configuration/subscription');
    }
  };

  const handleClose = () => {
    hasPermission
      ? navigate('/system-configuration/subscription')
      : navigate('/quality-management');
  };

  useEffect(() => {
    const { isDraftActive, isSmartMonitoringActive } = subscriptionsStore;

    if (
      !isDraftActive &&
      !isSmartMonitoringActive &&
      noSubscriptionsBlockedRoutes.includes(location.pathname)
    ) {
      setOpenSubscriptionBlockedModal(true);
    } else if (
      !isDraftActive &&
      isSmartMonitoringActive &&
      smartMonitoringBlockedRoutes.includes(location.pathname)
    ) {
      setOpenSubscriptionBlockedModal(true);
      if (firstAttempt) {
        navigate('/quality-management');
        setFirstAttempt(false);
      }
    } else {
      setOpenSubscriptionBlockedModal(false);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location, subscriptionsStore, firstAttempt]);

  const body =
    !subscriptionsStore.isDraftActive && !subscriptionsStore.isSmartMonitoringActive ? (
      <StyledBox sx={({ spacing }) => ({ padding: spacing(5) })}>
        <Box className="icon_box">
          <NotFoundIcon className="spaceman_icon" />
        </Box>
        <Typography sx={{ margin: '32px 0' }} align="center" variant="h4">
          {t('layouts.noValidSubscription')}
        </Typography>
        <Typography
          sx={({ palette }) => ({
            fontSize: '16px',
            fontWeight: 500,
            color: palette.whiteEmphasis.high,
          })}
        >
          {t('layouts.noValidSubscriptionDescription')}
        </Typography>

        <List className="list">
          <ListItem>
            <Typography>
              <b>{t('layouts.smartDraft')}:</b> {t('layouts.smartDraftDescription')}
            </Typography>
          </ListItem>
          <ListItem>
            <Typography>
              <b>{t('layouts.smartCooling')}:</b> {t('layouts.smartCoolingDescription')}
            </Typography>
          </ListItem>
        </List>

        <Typography
          sx={({ palette }) => ({
            fontSize: '16px',
            fontWeight: 500,
            color: palette.whiteEmphasis.high,
          })}
        >
          {t('layouts.selectPlanToContinue')}
        </Typography>

        <Box className="controls no_subscription">
          <Button
            variant="contained"
            onClick={() => navigate('/system-configuration/subscription')}
          >
            {t('layouts.seeSubscriptions')}
          </Button>
        </Box>
      </StyledBox>
    ) : (
      <StyledBox>
        <Box className="image" sx={{ backgroundImage: `url(${details.smartDraft.image})` }} />
        <Box className="body">
          <Box className="title_box">
            <Typography component="span" className="title">
              {subscriptionsStore.subscriptionNameByType('smartDraft')}{' '}
            </Typography>
            <Typography component="span" className="title" sx={{ fontWeight: '500 !important' }}>
              {t('layouts.subscriptionRequired')}
            </Typography>
          </Box>
          <Typography className="description">{text.smartDraft?.details?.description}</Typography>
        </Box>
        <Box className="controls">
          <Button variant="contained" onClick={handleMoreDetails}>
            {t('layouts.moreDetails')}
          </Button>
          <Button variant="outlined" onClick={handleClose}>
            {t('layouts.dismiss')}
          </Button>
        </Box>
      </StyledBox>
    );

  return isTabletOrSmaller ? (
    <StyledRootDrawer open={openSubscriptionBlockedModal} onClose={handleClose} anchor="bottom">
      {body}
      <FullDetails
        open={openFullDetails}
        type={'smartMonitoringCooling'}
        onClose={() => setOpenFullDetails(false)}
      />
    </StyledRootDrawer>
  ) : (
    <StyledDialog
      container={() => document.getElementById('main')}
      open={openSubscriptionBlockedModal}
      onClose={handleClose}
      fullWidth
      disableAutoFocus={true}
      slotProps={{
        root: {
          sx: { position: 'absolute', height: 'calc(100vh - 100px)' },
        },
        container: {
          sx: { position: 'absolute' },
        },
        backdrop: {
          sx: { position: 'absolute' },
        },
      }}
    >
      <DialogContent>{body}</DialogContent>
    </StyledDialog>
  );
};

export default SubscriptionDetailsModal;

const StyledDialog = styled(Dialog)(({ theme: { spacing, palette } }) => ({
  '& .MuiDialogContent-root': {
    padding: '0 !important',
    backgroundColor: palette.secondary.greyLight,
  },
}));

const StyledRootDrawer = styled(Drawer)(({ theme: { palette } }) => ({
  '& .MuiDrawer-paper': {
    backgroundColor: palette.secondary.greyLight,
    borderRadius: '16px 16px 0 0',
  },
}));

const StyledBox = styled(Box)(({ theme: { spacing, palette, breakpoints } }) => ({
  '.image': {
    width: '100%',
    height: '256px',
    display: 'flex',
    justifyContent: 'center',
    alignContent: 'center',
    alignItems: 'center',
    backgroundSize: 'cover',
    backgroundRepeat: 'no-repeat',
    backgroundPosition: 'top',
    backgroundColor: '#141414',

    [breakpoints.down('md')]: {
      height: '166px',
      backgroundSize: 'contain',
    },
  },

  '.icon_box': {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    marginBottom: spacing(2),
  },

  '.spaceman_icon': {},

  '.list': {
    listStyleType: 'disc',
    paddingLeft: spacing(2),

    '& > li': {
      display: 'list-item',
      paddingLeft: 0,

      '& > p': {
        fontSize: '16px',
        fontWeight: 500,
        color: palette.whiteEmphasis.high,
      },
    },
  },

  '.body': {
    padding: spacing(4),

    [breakpoints.down('md')]: {
      padding: spacing(2),
    },

    '.title_box': {
      marginBottom: spacing(2),
      display: 'flex',
      flexDirection: 'column',
      alignItems: 'center',
      justifyContent: 'center',
      textAlign: 'center',
    },

    '.title': {
      fontSize: '26px',
      fontWeight: 700,
      color: palette.whiteEmphasis.high,

      [breakpoints.down('md')]: {
        textAlign: 'center',
      },
    },
    '.description': {
      fontSize: '16px',
      fontWeight: 500,
      color: palette.whiteEmphasis.high,
    },
  },

  '.controls': {
    display: 'flex',
    flexDirection: 'column',
    rowGap: spacing(2),
    alignItems: 'center',
    marginBottom: spacing(4),

    '&.no_subscription': {
      marginBottom: 0,
      marginTop: spacing(2),
    },

    [breakpoints.down('md')]: {
      padding: spacing(2),
      marginBottom: spacing(0),
    },

    button: {
      textTransform: 'none',
      width: '300px',
      [breakpoints.down('md')]: {
        width: '100%',
      },
    },
  },
  '.hidden': {
    display: 'none',
  },
}));
